import { ComponentFacetKey, ComponentType } from 'models';

import { CONVERTER_CATEGORIES, ConverterCategoriesPreset } from './converter-categories';

export type VoltagePreset = { label: string; min?: number; nom?: number; max?: number };
export type EnergyPreset = { label: string; suffix: string; value: number; unit: 'kWh' };
export type PowerPreset = { label: string; suffix?: string; nom: number };
export type CurrentPreset = { label: string; nom: number };
export type BreakingCapacityPreset = { label: string; value: number };

export const VOLTAGE_PRESETS: VoltagePreset[] = [
    { label: '350 V', min: 320, nom: 350, max: 380 },
    { label: '700 V', min: 640, nom: 700, max: 760 },
    { label: '800 V', min: 720, nom: 800, max: 880 },
    { label: '1400 V', min: 1280, nom: 1400, max: 1500 },
    { label: '1500 V', min: 1350, nom: 1500, max: 1650 },
];

export const SOLAR_VOLTAGE_PRESETS: VoltagePreset[] = [
    { label: '≥ 20 V', nom: 20 },
    { label: '≥ 30 V', nom: 30 },
    { label: '≥ 40 V', nom: 40 },
    { label: '≥ 50 V', nom: 50 },
];

export const ENERGY_PRESETS: EnergyPreset[] = [
    { label: 'Small', suffix: '~10 kWh', value: 10, unit: 'kWh' },
    { label: 'Medium', suffix: '~100 kWh', value: 100, unit: 'kWh' },
    { label: 'Large', suffix: '~500 kWh', value: 500, unit: 'kWh' },
    { label: 'Utility Scale', suffix: '2+ MWh', value: 2000, unit: 'kWh' },
];

export const POWER_PRESETS: PowerPreset[] = [
    { label: 'Small', suffix: '≥ 1 kW', nom: 1000 },
    { label: 'Medium', suffix: '≥ 25 kW', nom: 25000 },
    { label: 'Large', suffix: '≥ 250 kW', nom: 250000 },
    { label: 'Utility', suffix: '≥ 2 MW', nom: 2000000 },
];

export const SOLAR_POWER_PRESETS: PowerPreset[] = [
    { label: '≥ 200 W', nom: 200 },
    { label: '≥ 300 W', nom: 300 },
    { label: '≥ 400 W', nom: 400 },
    { label: '≥ 500 W', nom: 500 },
    { label: '≥ 600 W', nom: 600 },
    { label: '≥ 700 W', nom: 700 },
];

export const FUSE_CURRENT_PRESETS: CurrentPreset[] = [
    { label: '32 A', nom: 32 },
    { label: '63 A', nom: 63 },
    { label: '125 A', nom: 125 },
    { label: '250 A', nom: 250 },
    { label: '630 A', nom: 630 },
];

export const FUSE_BREAKING_CAPACITY_PRESETS: BreakingCapacityPreset[] = [
    { label: '100 A', value: 100 },
    { label: '250 A', value: 250 },
    { label: '500 A', value: 500 },
    { label: '1000 A', value: 1000 },
];

export const BREAKER_BREAKING_CAPACITY_PRESETS: BreakingCapacityPreset[] = [
    { label: '5 kA', value: 5000 },
    { label: '10 kA', value: 10000 },
    { label: '35 kA', value: 35000 },
    { label: '100 kA', value: 100000 },
];

export const POLES_PRESETS = [1, 2, 3, 4];

export type FilterSectionDef =
    | { kind: 'voltage'; label: string; presets: VoltagePreset[] }
    | { kind: 'energyCapacity'; label: string; presets: EnergyPreset[] }
    | { kind: 'power'; label: string; presets: PowerPreset[]; unit?: 'W' | 'kW' }
    | { kind: 'converterCategories'; presets: ConverterCategoriesPreset[] }
    | { kind: 'batteryTechnologies'; label: string }
    | { kind: 'solarTechnologies' }
    | { kind: 'openingTime'; label: string }
    | { kind: 'breakerType' }
    | { kind: 'current'; label: string; presets: CurrentPreset[] }
    | { kind: 'fuseMinBreakingCapacity'; label: string; presets: BreakingCapacityPreset[] }
    | { kind: 'breakerMinBreakingCapacity'; label: string; presets: BreakingCapacityPreset[] }
    | { kind: 'fusePoles'; label: string; presets: number[] }
    | { kind: 'converterVoltage'; presets: VoltagePreset[] }
    | { kind: 'isolated' }
    | { kind: 'phase' }
    | { kind: 'other'; label: string; items: Array<'compatibleWith'> };

export const FILTER_CONFIG: Partial<Record<ComponentType, FilterSectionDef[]>> = {
    battery: [
        { kind: 'other', label: 'Compatibility', items: ['compatibleWith'] },
        { kind: 'batteryTechnologies', label: 'Chemistry' },
        { kind: 'voltage', label: 'Voltage', presets: VOLTAGE_PRESETS },
        { kind: 'energyCapacity', label: 'Capacity', presets: ENERGY_PRESETS },
    ],
    solution: [
        { kind: 'energyCapacity', label: 'Capacity', presets: ENERGY_PRESETS },
        { kind: 'voltage', label: 'Voltage', presets: VOLTAGE_PRESETS },
    ],
    solar: [
        { kind: 'solarTechnologies' },
        { kind: 'power', label: 'Power', presets: SOLAR_POWER_PRESETS, unit: 'W' },
        { kind: 'voltage', label: 'Voltage', presets: SOLAR_VOLTAGE_PRESETS },
    ],
    converter: [
        { kind: 'other', label: 'Compatibility', items: ['compatibleWith'] },
        { kind: 'converterCategories', presets: CONVERTER_CATEGORIES },
        { kind: 'power', label: 'Power', presets: POWER_PRESETS, unit: 'kW' },
        { kind: 'converterVoltage', presets: VOLTAGE_PRESETS },
        { kind: 'isolated' },
        { kind: 'phase' },
    ],
    breaker: [
        { kind: 'breakerType' },
        { kind: 'voltage', label: 'Voltage', presets: VOLTAGE_PRESETS },
        { kind: 'openingTime', label: 'Opening Time' },
        { kind: 'breakerMinBreakingCapacity', label: 'Breaking Capacity', presets: BREAKER_BREAKING_CAPACITY_PRESETS },
        { kind: 'fusePoles', label: 'Poles', presets: POLES_PRESETS },
    ],
    fuse: [
        { kind: 'voltage', label: 'Voltage', presets: VOLTAGE_PRESETS },
        { kind: 'current', label: 'Rated Current', presets: FUSE_CURRENT_PRESETS },
        { kind: 'fuseMinBreakingCapacity', label: 'Breaking Capacity', presets: FUSE_BREAKING_CAPACITY_PRESETS },
    ],
};

export const CARD_SECTION_KINDS = new Set<FilterSectionDef['kind']>([
    'solarTechnologies',
    'converterCategories',
    'breakerType',
]);

/** Facets the shared sidebar renders, whatever product type is selected. */
const SIDEBAR_FACET_KEYS: ComponentFacetKey[] = ['manufacturers', 'regions', 'compliances'];

const FACET_KEY_BY_SECTION_KIND: Record<FilterSectionDef['kind'], ComponentFacetKey | null> = {
    batteryTechnologies: 'batteryTechnologies',
    breakerType: 'breakerTypes',
    openingTime: 'openingTimes',
    other: 'compatibleWith',
    solarTechnologies: 'solarTechnologies',

    breakerMinBreakingCapacity: null,
    converterCategories: null,
    converterVoltage: null,
    current: null,
    energyCapacity: null,
    fuseMinBreakingCapacity: null,
    fusePoles: null,
    isolated: null,
    phase: null,
    power: null,
    voltage: null,
};

export const getFacetKeysForType = (type?: ComponentType | null): ComponentFacetKey[] => [
    ...SIDEBAR_FACET_KEYS,
    ...(FILTER_CONFIG[type as ComponentType] ?? [])
        .map((section) => FACET_KEY_BY_SECTION_KIND[section.kind])
        .filter((key): key is ComponentFacetKey => key !== null),
];

export const isSelectedPreset = (
    customValue: number | '',
    portValues?: { min?: number; nom?: number; max?: number },
    preset?: { min?: number; nom?: number; max?: number },
) => {
    return (
        customValue === '' &&
        portValues?.min === preset?.min &&
        portValues?.nom === preset?.nom &&
        portValues?.max === preset?.max
    );
};

export const getPresetValue = (preset?: { min?: number; nom?: number; max?: number }) => {
    if (preset?.nom) return preset.nom;
    if (preset?.min) return preset.min;
    if (preset?.max) return preset.max;
    return undefined;
};
